import { Box, Typography } from '@material-ui/core';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import L from 'leaflet';
import {
  LayersControl,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  Tooltip,
  useMap
} from 'react-leaflet';
import { FullscreenControl } from 'react-leaflet-fullscreen';
import LicensePlateTag from 'src/app/components/licensePlateTag/LicesePlateTag';
import ContentCarInfo from 'src/app/components/popupInfocar/ContentCarInfo';
import RotateIcon from 'src/app/utils/RotateIcon';
import { getIconStatus } from 'src/app/utils/vehicleService';
import mkIcon from '../../assets/mkicon.png';
import './style.css';
import { STATUS_API } from 'src/app/constant/config';
import { getDetailVehicle } from 'src/features/vehicleSlice';
import useSupercluster from 'use-supercluster';

const INTERVAL = 10000;
const MAX_ZOOM = 22;
const MIN_ZOOM = 6;
const DEFAULT_CENTER = [21.024683, 105.832495];
const POPUP_OFFSET = [0, -35];
const TOOLTIP_OFFSET = [0, -35];
const CLUSTER_TRANSITION_DURATION = 500;

//#region Display Icons
const handleDisplayIcon = vehicle => {
  if (!vehicle) {
    return L.icon({
      iconUrl: '/static/iconSvg/cars/car_lost_gsm.svg',
      iconAnchor: [0, 0]
    });
  }
  if (vehicle.debt === true) {
    return L.icon({
      iconUrl: '/static/iconSvg/motors/motor_expired.svg',
      iconAnchor: [16, 16]
    });
  }
  const iconVehicle = getIconStatus(
    vehicle.device_status,
    vehicle.vehicle_type
  );
  let makerIcon;
  if (vehicle) {
    let iconObject = RotateIcon.makeIcon(iconVehicle);
    if (typeof iconObject.setRotation === 'function') {
      iconObject = iconObject.setRotation({
        deg: vehicle.direction ? vehicle.direction : 0.001
      });
    }
    const url =
      typeof iconObject.getUrl === 'function' ? iconObject.getUrl() : mkIcon;
    const validUrl = url && url.startsWith('data:image') ? url : iconVehicle;

    makerIcon = L.icon({
      iconUrl: validUrl,
      iconAnchor: [16, 16]
    });
  }
  return (
    makerIcon ||
    L.icon({
      iconUrl: '/static/iconSvg/cars/car_lost_gsm.svg',
      iconAnchor: [0, 0]
    })
  );
};

const ResetView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    const interval = setInterval(() => {
      map.setView([center[0] - 0.002, center[1]]);
    }, INTERVAL);
    return () => clearInterval(interval);
  }, [center, map]);
  return null;
};

const VehicleMarker = React.memo(
  ({
    vehicle,
    position,
    isSelected,
    isInViewport,
    onClick,
    markerRef,
    vehicleIconsRef,
    positionsInfoBox,
    detailedVehicleInfo,
    isLoadingDetail,
    opacity = 1
  }) => {
    const icon = handleDisplayIcon(vehicle);

    return (
      <Marker
        position={position}
        zIndexOffset={isSelected ? 1000 : 0}
        eventHandlers={{ click: () => onClick(vehicle) }}
        icon={icon}
        opacity={opacity}
        ref={ref => {
          if (ref) {
            markerRef.current[vehicle.device_id] = ref;
            if (!vehicleIconsRef.current[vehicle.device_id]) {
              vehicleIconsRef.current[vehicle.device_id] = icon;
            }
          }
        }}
      >
        <Popup
          offset={POPUP_OFFSET}
          // autoPanPaddingTopLeft={[420, 30]}
          // autoPanPaddingBottomRight={[30, 110]}
          autoPan={true}
          maxWidth={350}
          minWidth={250}
          maxHeight={400}
        >
          <ContentCarInfo
            positionsInfoBox={positionsInfoBox || {}}
            detailedVehicleInfo={detailedVehicleInfo || {}}
            isLoadingDetail={isLoadingDetail}
          />
        </Popup>
        {(isInViewport || isSelected) && opacity > 0 && (
          <Tooltip
            direction="center"
            offset={TOOLTIP_OFFSET}
            autoPan={true}
            opacity={1}
            permanent={true}
          >
            <Box style={{ marginTop: '0px' }}>
              <LicensePlateTag
                isSelected={isSelected}
                position={vehicle}
                license_plate={vehicle.license_plate}
                color={isSelected ? 'white' : '#000000'}
                backgroundColor={isSelected ? '#D9042F' : '#F5FAFF'}
              />
            </Box>
          </Tooltip>
        )}
        {isSelected && (
          <ResetView center={[Number(vehicle.lat), Number(vehicle.lng)]} />
        )}
      </Marker>
    );
  },
  (prevProps, nextProps) =>
    prevProps.vehicle.device_id === nextProps.vehicle.device_id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isInViewport === nextProps.isInViewport &&
    _.isEqual(prevProps.positionsInfoBox, nextProps.positionsInfoBox) &&
    _.isEqual(prevProps.detailedVehicleInfo, nextProps.detailedVehicleInfo) &&
    prevProps.isLoadingDetail === nextProps.isLoadingDetail &&
    prevProps.opacity === nextProps.opacity
);

function MapOsmCamera({ listCamera4GTracking, showMenu, deviceSerial }) {
  const dispatch = useDispatch();
  const [center, setCenter] = useState(() => {
    const centerString = localStorage.getItem('center');
    if (centerString) {
      const cleanedString = centerString.replace(/[()]/g, '').trim();
      const centerArray = cleanedString
        .split(',')
        .map(coord => Number(coord.trim()));
      if (
        centerArray.length === 2 &&
        !isNaN(centerArray[0]) &&
        !isNaN(centerArray[1]) &&
        centerArray[0] >= -90 &&
        centerArray[0] <= 90 &&
        centerArray[1] >= -180 &&
        centerArray[1] <= 180
      ) {
        return centerArray;
      }
    }
    return DEFAULT_CENTER;
  });
  const [positionsInfoBox, setPositionsInfoBox] = useState({});
  const [mapRef, setMapRef] = useState(null);
  const markerRef = useRef({});
  const [currentLocation, setCurrentLocation] = useState(null);
  const [vehicleSelected, setVehicleSelected] = useState({});
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailedVehicleInfo, setDetailedVehicleInfo] = useState({});

  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(13);
  const [carStatus, setCarStatus] = useState(null);

  const animationRef = useRef({});
  const vehiclePositionsRef = useRef({});
  const lastKnownPositionsRef = useRef({});
  const vehicleIconsRef = useRef({});
  const clusterPositionsRef = useRef({});
  const lastUpdateRef = useRef({});

  const updateBoundsAndZoom = useCallback(
    _.debounce(() => {
      if (!mapRef) return;
      const mapBounds = mapRef.getBounds();
      setBounds([
        Math.min(mapBounds.getWest(), -180),
        Math.min(mapBounds.getSouth(), -90),
        Math.max(mapBounds.getEast(), 180),
        Math.max(mapBounds.getNorth(), 90)
      ]);
      setZoom(mapRef.getZoom());
    }, 200),
    [mapRef]
  );

  const points = React.useMemo(() => {
    return listCamera4GTracking.map((vehicle, index) => {
      const lat = Number(vehicle.lat) || 0;
      const lng = Number(vehicle.lng) || 0;
      return {
        type: 'Feature',
        properties: {
          cluster: false,
          vehicle: { ...vehicle, lat, lng },
          pointId: index
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      };
    });
  }, [listCamera4GTracking]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 60, maxZoom: 18, minZoom: MIN_ZOOM }
  });

  const clusterCounts = React.useMemo(() => {
    const counts = {};
    const singleVehicles = {};
    clusters.forEach(cluster => {
      if (!cluster.properties.cluster) return;
      const clusterId = cluster.properties.cluster_id;
      const leaves = supercluster.getLeaves(clusterId, Infinity);
      const filteredLeaves = leaves.filter(leaf => {
        const vehicle = leaf.properties.vehicle;
        if (vehicleSelected.device_id === vehicle.device_id) {
          return false;
        }
        let matchesCarStatus =
          !carStatus ||
          vehicle.device_status?.toLowerCase() === carStatus.toLowerCase();
        let matchesDeviceType = vehicle.vehicle_type === 'VGPS_CAM4G';
        return matchesCarStatus && matchesDeviceType;
      });
      counts[clusterId] = filteredLeaves.length;
      if (filteredLeaves.length === 1) {
        singleVehicles[clusterId] = filteredLeaves[0].properties.vehicle;
      }
    });
    return { counts, singleVehicles };
  }, [clusters, supercluster, carStatus, vehicleSelected]);

  const visibleClusters = React.useMemo(() => {
    if (!mapRef || !clusters) return [];
    const bounds = mapRef.getBounds();
    const result = clusters.filter(cluster => {
      const [lng, lat] = cluster.geometry.coordinates;
      return bounds.contains([lat, lng]);
    });
    return result;
  }, [clusters, mapRef]);

  useEffect(() => {
    if (!listCamera4GTracking || listCamera4GTracking.length === 0 || !mapRef)
      return;

    const bounds = mapRef.getBounds();
    const visibleMarkers = {};
    listCamera4GTracking.forEach(vehicle => {
      const deviceId = vehicle.device_id;
      const lat = Number(vehicle.lat) || 0;
      const lng = Number(vehicle.lng) || 0;
      visibleMarkers[deviceId] = bounds.contains([lat, lng]);
    });

    clusterPositionsRef.current = {};
    clusters.forEach(cluster => {
      if (!cluster.properties.cluster) return;
      const clusterPosition = [
        cluster.geometry.coordinates[1],
        cluster.geometry.coordinates[0]
      ];
      const leaves = supercluster.getLeaves(
        cluster.properties.cluster_id,
        Infinity
      );
      leaves.forEach(leaf => {
        const deviceId = leaf.properties.vehicle.device_id;
        if (vehiclePositionsRef.current[deviceId]) {
          lastKnownPositionsRef.current[deviceId] =
            vehiclePositionsRef.current[deviceId];
        }
        clusterPositionsRef.current[deviceId] = clusterPosition;
      });
    });

    listCamera4GTracking.forEach(vehicle => {
      const deviceId = vehicle.device_id;
      const lat = Number(vehicle.lat) || 0;
      const lng = Number(vehicle.lng) || 0;
      const newPosition = [lat, lng];

      const isInViewport = visibleMarkers[deviceId];
      const isSelected = vehicleSelected.device_id === deviceId;
      if (!isInViewport && !isSelected) {
        vehiclePositionsRef.current[deviceId] = newPosition;
        return;
      }

      const wasInCluster = !!clusterPositionsRef.current[deviceId];
      const oldPosition = vehiclePositionsRef.current[deviceId] || newPosition;
      const lastKnownPosition =
        lastKnownPositionsRef.current[deviceId] || oldPosition;
      const isInCluster = !!clusterPositionsRef.current[deviceId];

      if (isInCluster) {
        vehiclePositionsRef.current[deviceId] = newPosition;
        return;
      }

      const isSamePosition =
        oldPosition[0] === newPosition[0] && oldPosition[1] === newPosition[1];
      if (isSamePosition && !wasInCluster && !isInCluster) {
        vehiclePositionsRef.current[deviceId] = newPosition;
        if (markerRef.current[deviceId]) {
          markerRef.current[deviceId].setLatLng(newPosition);
        }
        return;
      }

      const isSameAsLastKnown =
        lastKnownPosition[0] === newPosition[0] &&
        lastKnownPosition[1] === newPosition[1];
      if (wasInCluster && !isInCluster && isSameAsLastKnown) {
        vehiclePositionsRef.current[deviceId] = newPosition;
        if (markerRef.current[deviceId]) {
          markerRef.current[deviceId].setLatLng(newPosition);
        }
        return;
      }

      const newIcon = handleDisplayIcon(vehicle);
      if (markerRef.current[deviceId]) {
        const currentIcon = vehicleIconsRef.current[deviceId];
        if (
          !currentIcon ||
          currentIcon.options.iconUrl !== newIcon.options.iconUrl
        ) {
          markerRef.current[deviceId].setIcon(newIcon);
          vehicleIconsRef.current[deviceId] = newIcon;
        }
      }

      const startPosition = wasInCluster ? lastKnownPosition : oldPosition;
      const duration =
        wasInCluster && !isInCluster ? CLUSTER_TRANSITION_DURATION : INTERVAL;

      if (animationRef.current[deviceId]) {
        cancelAnimationFrame(animationRef.current[deviceId]);
      }

      const startTime = performance.now();
      lastUpdateRef.current[deviceId] = startTime;

      const animate = currentTime => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const interpolatedLat =
          startPosition[0] + (newPosition[0] - startPosition[0]) * progress;
        const interpolatedLng =
          startPosition[1] + (newPosition[1] - startPosition[1]) * progress;

        vehiclePositionsRef.current[deviceId] = [
          interpolatedLat,
          interpolatedLng
        ];
        if (markerRef.current[deviceId] && (isInViewport || isSelected)) {
          markerRef.current[deviceId].setLatLng([
            interpolatedLat,
            interpolatedLng
          ]);
        }

        if (progress < 1) {
          animationRef.current[deviceId] = requestAnimationFrame(animate);
        } else {
          vehiclePositionsRef.current[deviceId] = newPosition;
        }
      };
      animationRef.current[deviceId] = requestAnimationFrame(animate);
      vehiclePositionsRef.current[deviceId] = startPosition;
    });

    return () => {
      Object.values(animationRef.current).forEach(cancelAnimationFrame);
    };
  }, [listCamera4GTracking, mapRef, clusters, supercluster, vehicleSelected]);

  const detailVehicle = useSelector(state => state.vehicleSlice.detailVehicle);
  const statusGetDetail = useSelector(
    state => state.vehicleSlice.statusGetDetail
  );

  useEffect(() => {
    if (!vehicleSelected.device_id) {
      setDetailedVehicleInfo({});
      setIsLoadingDetail(false);
      return;
    }

    const fetchDetailVehicle = () => {
      setIsLoadingDetail(true);
      dispatch(getDetailVehicle({ id: vehicleSelected.device_id }));
    };

    fetchDetailVehicle();
    const intervalId = setInterval(fetchDetailVehicle, 10000);

    return () => clearInterval(intervalId);
  }, [vehicleSelected.device_id]);

  useEffect(() => {
    const marker = markerRef.current[vehicleSelected.device_id];
    if (marker && !marker.isPopupOpen()) {
      marker.openPopup();
    }
  }, [vehicleSelected?.device_id]);

  useEffect(() => {
    if (!vehicleSelected.device_id) {
      setDetailedVehicleInfo({});
      setIsLoadingDetail(false);
      return;
    }

    if (statusGetDetail === STATUS_API.PENDING) {
      setIsLoadingDetail(true);
      const timeoutId = setTimeout(() => {
        setIsLoadingDetail(false);
      }, 10000);
      return () => clearTimeout(timeoutId);
    } else if (statusGetDetail === STATUS_API.SUCCESS && detailVehicle) {
      const vehicleData =
        detailVehicle.vehicle ||
        detailVehicle.payload?.vehicle ||
        detailVehicle;
      if (!vehicleData) {
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        return;
      }
      setDetailedVehicleInfo({
        ...vehicleData,
        lat: Number(vehicleData.lat),
        lng: Number(vehicleData.lng)
      });
      setIsLoadingDetail(false);
    } else if (statusGetDetail === STATUS_API.ERROR) {
      setDetailedVehicleInfo(prev => ({
        ...prev,
        daily_distance: 0
      }));
      setIsLoadingDetail(false);
    }
  }, [detailVehicle, statusGetDetail, vehicleSelected.device_id]);

  useEffect(() => {
    if (deviceSerial) {
      const vehicle = findVehicle(deviceSerial);
      if (vehicle) {
        setVehicleSelected(vehicle);
        getVehicle(vehicle);
      }
    }
  }, [deviceSerial]);

  const findVehicle = deviceSerial => {
    const device = listCamera4GTracking.find(vehicle => {
      return vehicle.device_serial === deviceSerial;
    });

    return device;
  };

  useEffect(() => {
    if (mapRef) {
      setTimeout(() => {
        mapRef.invalidateSize();
      }, 300);
    }
  }, [mapRef, showMenu]);

  const handleMarkerClick = vehicle => {
    if (vehicle) {
      if (vehicleSelected.device_id === vehicle.device_id) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        setDetailedVehicleInfo({});
        setIsLoadingDetail(false);
        if (markerRef.current[vehicle.device_id]) {
          markerRef.current[vehicle.device_id].closePopup();
        }
      } else {
        getVehicle(vehicle);
        setVehicleSelected(vehicle);
        setDetailedVehicleInfo({});
        setIsLoadingDetail(true);
        setPositionsInfoBox({
          ...vehicle,
          lat: Number(vehicle.lat),
          lng: Number(vehicle.lng)
        });
        setCenter([Number(vehicle.lat), Number(vehicle.lng)]);
        mapRef.setView([Number(vehicle.lat), Number(vehicle.lng)], 18);
        if (markerRef.current[vehicle.device_id]) {
          markerRef.current[vehicle.device_id].openPopup();
        }
      }
    }
  };

  const getVehicle = vehicle => {
    if (vehicle) {
      if (vehicleSelected?.device_id === vehicle?.device_id) {
        setVehicleSelected({});
        setPositionsInfoBox({});
        if (markerRef.current[vehicle.device_id]) {
          markerRef.current[vehicle.device_id].closePopup();
        }
      } else {
        setVehicleSelected(vehicle);
        setPositionsInfoBox({
          ...vehicle,
          lat: Number(vehicle.lat),
          lng: Number(vehicle.lng)
        });
        setCenter([Number(vehicle.lat), Number(vehicle.lng)]);
        mapRef?.setView([Number(vehicle.lat), Number(vehicle.lng)], 18);
        if (markerRef.current[vehicle.device_id]) {
          markerRef.current[vehicle.device_id].openPopup();
        }
      }
    }
  };

  useEffect(() => {
    if (!mapRef) return;
    updateBoundsAndZoom();
    mapRef.on('moveend', updateBoundsAndZoom);
    return () => mapRef.off('moveend', updateBoundsAndZoom);
  }, [mapRef, updateBoundsAndZoom]);

  const displayClusterIcon = cluster => {
    const clusterId = cluster.properties.cluster_id;
    const count = clusterCounts.counts[clusterId] || 0;
    if (count <= 1) return null;

    let backgroundColor = '#1976d2';
    let textColor = '#212529';
    if (count < 10) {
      backgroundColor = '#00ABE1CC';
    } else if (count <= 50) {
      backgroundColor = '#F9CF26CC';
    } else {
      backgroundColor = '#FF7E55CC';
    }

    const size = count < 100 ? 40 : count < 1000 ? 50 : 60;
    return L.divIcon({
      html: `<div style="background-color: ${backgroundColor}; font-size: 14px; width: ${size}px; height: ${size}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: ${textColor}; font-weight: bold;">${count}</div>`,
      className: 'custom-cluster-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };

  const displayLocationIcon = () => {
    let locationCurrent = L.icon({
      iconUrl: `/static/iconSvg/gps.svg`,
      iconSize: [25, 25],
      iconAnchor: [1, 25]
    });
    return locationCurrent;
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        setCurrentLocation([
          position.coords.latitude,
          position.coords.longitude
        ]);

        if (!mapRef) return;
        mapRef.flyTo(
          [position.coords.latitude, position.coords.longitude],
          13,
          {
            animate: true
          }
        );
      });
    }
  };

  const renderSelectedVehicleMarker = () => {
    if (
      !vehicleSelected.device_id ||
      (!vehicleSelected.lat && vehicleSelected.lat !== 0) ||
      (!vehicleSelected.lng && vehicleSelected.lng !== 0)
    ) {
      return null;
    }

    const isVisible = !carStatus || carStatus === vehicleSelected.device_status;
    if (!isVisible) return null;

    const markerPosition = vehiclePositionsRef.current[
      vehicleSelected.device_id
    ] || [Number(vehicleSelected.lat), Number(vehicleSelected.lng)];

    return (
      <VehicleMarker
        key={`selected-${vehicleSelected.device_id}`}
        vehicle={vehicleSelected}
        position={markerPosition}
        isSelected={true}
        isInViewport={mapRef && mapRef.getBounds().contains(markerPosition)}
        onClick={handleMarkerClick}
        markerRef={markerRef}
        vehicleIconsRef={vehicleIconsRef}
        positionsInfoBox={positionsInfoBox}
        detailedVehicleInfo={detailedVehicleInfo}
        isLoadingDetail={isLoadingDetail}
        opacity={1}
      />
    );
  };

  return (
      <MapContainer
        center={center}
        zoom={zoom}
        maxZoom={20}
        minZoom={6}
        style={{height:'calc(100vh - 66px)'}}
        zoomControl={false}
        scrollWheelZoom={true}
        whenCreated={mapR => setMapRef(mapR)}
        zoomAnimation={true}
      >
        <Box
          style={{
            position: 'absolute',
            bottom: '80px',
            right: '22px',

            cursor: 'pointer',
            boxShadow: 'rgba(20, 20, 20, 0.322)  0px 0px 2px 0px',
            border: '0px !important',

            padding: '8px',
            color: 'red',
            width: '48px',
            height: '48px',

            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            backgroundColor: 'white',
            borderRadius: '50%',
            zIndex: 1000
          }}
          onClick={() => getCurrentLocation()}
        >
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <img src="/static/iconSvg/locationIconv2.svg" alt="location" />
          </Box>
        </Box>

        <LayersControl position="bottomright">
          {/* <LayersControl.BaseLayer name="Open Street Map">
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          </LayersControl.BaseLayer> */}

          <LayersControl.BaseLayer name="Vệ tinh">
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=y&hl=vi&x={x}&y={y}&z={z}"
              subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
              maxZoom={20}
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked name="Google Map">
            <TileLayer
              url="https://{s}.google.com/vt/lyrs=m&hl=vi&x={x}&y={y}&z={z}"
              subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
              maxZoom={20}
            />
          </LayersControl.BaseLayer>
        </LayersControl>

        <Box style={{ marginRight: 10 }}>
          <FullscreenControl
            forceSeparateButton={true}
            position="topright"
            content='<img src="/static/iconSvg/fullscreen.svg" style="width: 24px; height: 24px;" />'
          />
        </Box>
        <>
          {visibleClusters.map((cluster, index) => {
            const [lng, lat] = cluster.geometry.coordinates;
            const position = [lat, lng];
            const {
              cluster: isCluster,
              cluster_id,
              vehicle
            } = cluster.properties;

            if (isCluster) {
              const count = clusterCounts.counts[cluster_id] || 0;
              if (count === 1) {
                const singleVehicle = clusterCounts.singleVehicles[cluster_id];
                if (!singleVehicle) return null;

                const isVisible =
                  !carStatus || carStatus === singleVehicle.device_status;
                const markerPosition = vehiclePositionsRef.current[
                  singleVehicle.device_id
                ] || [Number(singleVehicle.lat), Number(singleVehicle.lng)];
                const isInViewport =
                  mapRef && mapRef.getBounds().contains(markerPosition);

                return (
                  <VehicleMarker
                    key={singleVehicle.device_id}
                    vehicle={singleVehicle}
                    position={markerPosition}
                    isSelected={false}
                    isInViewport={isInViewport}
                    onClick={handleMarkerClick}
                    markerRef={markerRef}
                    vehicleIconsRef={vehicleIconsRef}
                    positionsInfoBox={positionsInfoBox}
                    detailedVehicleInfo={detailedVehicleInfo}
                    isLoadingDetail={isLoadingDetail}
                    opacity={isVisible ? 1 : 0}
                  />
                );
              }

              const clusterIcon = displayClusterIcon(cluster);
              if (!clusterIcon) return null;
              return (
                <Marker
                  key={`cluster-${cluster_id || index}`}
                  position={position}
                  icon={clusterIcon}
                  eventHandlers={{
                    click: () => {
                      mapRef.flyTo(position, zoom + 2, { animate: true });
                    }
                  }}
                />
              );
            }

            if (!vehicle) return null;

            if (vehicleSelected.device_id === vehicle.device_id) {
              return null;
            }

            const isVisible = !carStatus || carStatus === vehicle.device_status;
            const markerPosition = vehiclePositionsRef.current[
              vehicle.device_id
            ] || [Number(vehicle.lat), Number(vehicle.lng)];
            const isInViewport =
              mapRef && mapRef.getBounds().contains(markerPosition);

            return (
              <VehicleMarker
                key={vehicle.device_id}
                vehicle={vehicle}
                position={markerPosition}
                isSelected={false}
                isInViewport={isInViewport}
                onClick={handleMarkerClick}
                markerRef={markerRef}
                vehicleIconsRef={vehicleIconsRef}
                positionsInfoBox={positionsInfoBox}
                detailedVehicleInfo={detailedVehicleInfo}
                isLoadingDetail={isLoadingDetail}
                opacity={isVisible ? 1 : 0}
              />
            );
          })}
          {renderSelectedVehicleMarker()}
          {currentLocation && (
            <Marker icon={displayLocationIcon()} position={currentLocation}>
              <Tooltip
                style={{
                  background: 'rgba(0, 0, 0, 0) !important',
                  boxShadow: 'none !important'
                }}
                position={currentLocation}
                direction="center"
                permanent
              >
                <Box
                  style={{
                    backgroundColor: 'rgba(19, 19, 19, 0.493)',
                    color: 'white',
                    borderRadius: '5px',
                    padding: '2px',
                    position: 'absolute',
                    top: 5,
                    left: -22
                  }}
                >
                  {' '}
                  Vị trí của bạn
                </Box>
              </Tooltip>
            </Marker>
          )}
        </>
      </MapContainer>
  );
}

export default React.memo(MapOsmCamera);
